/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import retry from 'async-retry';
import _ from 'lodash';

import { allApi } from 'src/utils/axios';
import { authApi } from 'src/api/AuthApi';
import { clientApi } from 'src/api/clientApi';
import toClientPassword from 'src/lib/toClientPassword';
import logger from 'src/lib/logger';
import { useStorage } from 'src/redux/store';
import SocialAccountProvider from 'src/models/SocialAccountProvider';
import { openPopup, runPopup } from 'src/utils/modalWindow';
import { cleverConfig } from 'src/config';
import { SETTINGS_DICTATION_SCENARIO_PATH } from 'src/constants';

// Matches the `/auth/:provider/callback` route when it is loaded as a
// top-level navigation rather than inside the auth popup — i.e. a
// portal/IdP-initiated social login.
const SOCIAL_AUTH_CALLBACK_PATH = /^\/auth\/[^/]+\/callback\/?$/;

const isTopLevelSocialAuthCallback = () =>
  SOCIAL_AUTH_CALLBACK_PATH.test(window.location.pathname) &&
  !(window.opener && window.opener !== window);

const STORE_AUTH_SOURCE = {
  INIT: 'init',
  UNAUTHORIZED: 'unauthorized',
  LOGIN: 'login',
  SIGN_UP: 'sign_up',
  RESET_PASSWORD: 'reset_password',
};

const StorageKeys = {
  token: 'token',
  refreshToken: 'refreshToken',
  tokenIssuedAt: 'tokenIssuedAt',
};

const initialState = {
  isAuthenticated: false,
  user: undefined,
  tokenIssuedAt: undefined,
};

const TOKEN_LIFETIME = 24 * 3600;

let refreshTimer = null;

export const AuthContext = createContext(initialState);

export const AuthContextProvider = ({ children }) => {
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem(StorageKeys.token);
  const getRefreshToken = () => localStorage.getItem(StorageKeys.refreshToken);

  const storedToken = getToken();

  const [isInited, setIsInited] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!storedToken);
  const [tokenIssuedAt, setTokenIssuedAt] = useState(
    localStorage.getItem(StorageKeys.tokenIssuedAt),
  );
  if (storedToken) {
    allApi.forEach(
      (api) =>
        (api.defaults.headers.common['Authorization'] =
          `Bearer ${storedToken}`),
    );
  }

  const [user, setUser] = useState();
  const [userSettingsUpdating, setUserSettingsUpdating] = useState(false);
  const [userTasks, setUserTasks] = useState([]);

  const reduxStorage = useStorage();

  const clearRefreshTimer = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  };

  const scheduleTokenRefresh = (issuedAt) => {
    clearRefreshTimer();

    const expiresAt = issuedAt + TOKEN_LIFETIME * 1000;
    const now = Date.now();

    const msUntilExpire = expiresAt - now;

    // Refresh before expiration
    const msUntilRefresh = Math.max(msUntilExpire - 30000, 0);

    refreshTimer = setTimeout(async () => {
      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          setIsAuthenticated(false);
          navigate('/login');
          return;
        }

        const newIssuedAt = Date.now();
        const { data } = await authApi.refreshToken(refreshToken);
        if (data.reason) {
          logout({ redirectPath: null });
          return navigate('/login', {
            replace: true,
            state: {
              legal_restrictions: {
                reason: data.reason,
                details: data.details,
              },
            },
          });
        }
        await storeAuthResponse(data, newIssuedAt, STORE_AUTH_SOURCE.REFRESH);
      } catch (err) {
        console.error('[Auth] Token refresh failed:', err);
        setIsAuthenticated(false);
        navigate('/login');
      }
    }, msUntilRefresh);
  };

  const storeAuthResponse = async (data, issuedAt, source) => {
    logger.setDefaultInfo({
      user_id: data.user?.id,
      is_test_user: data.user?.is_test_user,
    });

    logger.info(
      `[AuthContext] storeAuthResponse id: ${data.user?.id} reason: ${source}`,
    );

    if (!data.token) {
      return;
    }

    localStorage.setItem(StorageKeys.token, data.token);
    localStorage.setItem(StorageKeys.refreshToken, data.refreshToken);
    localStorage.setItem(StorageKeys.tokenIssuedAt, issuedAt);
    setUser(data.user);
    setUserTasks(data.user.tasks);
    allApi.forEach(
      (api) =>
        (api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`),
    );

    if (STORE_AUTH_SOURCE.UNAUTHORIZED !== source) {
      await reduxStorage.loadInitData();
    }
    setIsAuthenticated(true);
    setTokenIssuedAt(issuedAt);

    scheduleTokenRefresh(issuedAt);
  };

  const resetPassword = async (email, update_token, password) => {
    const issuedAt = Date.now();
    const { data } = await authApi.resetPassword(email, update_token, password);
    await storeAuthResponse(data, issuedAt, STORE_AUTH_SOURCE.RESET_PASSWORD);
  };

  const signUp = async ({
    email,
    password,
    name,
    secondary_name,
    secondary_email,
    secondary_phone,
    birthdate,
    country,
    region_code,
    parent_email,
    sourceTracking,
    accepted_tos,
    accepted_privacy_policy,
    marketing_consent,
    support_person_consent,
    cookies_consent_types,
  }) => {
    const issuedAt = Date.now();
    const { data } = await authApi.signUp({
      email,
      hashedPassword: toClientPassword(password),
      appLanguage: 'en',
      name,
      secondary_name,
      secondary_email,
      secondary_phone,
      country,
      region_code,
      birthdate,
      primaryContact: 'user',
      parent_email,
      sourceTracking,
      accepted_tos,
      accepted_privacy_policy,
      marketing_consent,
      support_person_consent,
      cookies_consent_types,
    });
    await storeAuthResponse(data, issuedAt, STORE_AUTH_SOURCE.SIGN_UP);
    return data;
  };

  const login = async (email, password) => {
    const issuedAt = Date.now();
    const { data } = await authApi.login(email, toClientPassword(password));
    await storeAuthResponse(data, issuedAt, STORE_AUTH_SOURCE.LOGIN);
    return data;
  };

  const refetchData = async (refreshToken, source) => {
    let legalRestrictionData = null;

    try {
      await retry(
        async (bail) => {
          try {
            const issuedAt = Date.now();
            const { data } = await authApi.refreshToken(refreshToken);
            if (data.reason) {
              legalRestrictionData = {
                reason: data.reason,
                details: data.details,
              };
              bail(new Error('LEGAL_RESTRICTION'));
              return;
            }
            await storeAuthResponse(data, issuedAt, source);
          } catch (e) {
            console.log(e);
            if (e.status === 401) {
              bail(e);
            } else {
              throw e;
            }
          }
        },
        { retries: 3 },
      );
    } catch (e) {
      if (e.message === 'LEGAL_RESTRICTION' && legalRestrictionData) {
        logout({ redirectPath: null });
        return navigate('/login', {
          replace: true,
          state: { legal_restrictions: legalRestrictionData },
        });
      }

      const isNetworkError = !e.response && !e.status;
      if (isNetworkError) {
        const originalUrl = `${window.location.pathname}${window.location.search}`;
        const url = `/no_internet${originalUrl !== '/' ? `?online_redirect=${encodeURIComponent(originalUrl)}` : ''}`;
        navigate(url);
      } else {
        logger.error(`[AuthContext] Failed to refetch data: ${e.message}`);
        logout();
      }
    }
  };

  const updateUser = (updates) => {
    const newUser = {
      ...user,
      ...updates,
    };
    if (_.isEqual(newUser, user)) {
      return;
    }
    setUser(newUser);
  };

  const completeUserTask = async (task) => {
    await clientApi.completeTask(task);

    setUserTasks((userTasks) => (userTasks || []).filter((t) => t !== task));
  };

  const updateDictationScenario = async (scenario) => {
    const settingsUpdates = {};
    _.set(settingsUpdates, SETTINGS_DICTATION_SCENARIO_PATH, scenario);
    await updateUserSettings(settingsUpdates);
  };

  const updateUserSettings = async (updates) => {
    if (_.isEmpty(updates)) {
      return;
    }

    setUserSettingsUpdating(true);
    try {
      const settings = await clientApi.updateUserSettings(updates);
      updateUser({ settings });
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      setUserSettingsUpdating(false);
    }
  };

  const logout = async ({
    redirectPath = '/login',
    sendAuthEventLog = true,
  } = {}) => {
    if (sendAuthEventLog) {
      authApi.logout();
    }

    setIsAuthenticated(false);
    allApi.forEach(
      (api) => delete api.defaults.headers.common['Authorization'],
    );

    setUser(undefined);
    setUserTasks([]);
    reduxStorage.logout();
    logger.clear();

    Object.values(StorageKeys).forEach((key) => localStorage.removeItem(key));

    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  };

  const fetchUserTasks = async () => {
    const tasks = await clientApi.fetchUserTasks();
    if (!tasks) {
      return;
    }
    setUserTasks(tasks);
  };

  const getCallbackUrl = (provider) =>
    `${window.location.origin}/auth/${provider}/callback`;

  // Social auth is provider-generic: `provider` is a SocialAccountProvider
  // value ('google' | 'facebook' | 'clever' | ...). The same redirect_uri
  // (getCallbackUrl(provider)) is reused for the auth_url call and the
  // sign_in/sign_up call so it stays byte-identical, as the backend requires.
  const socialAuthLink = async (provider) => {
    const state = {};
    // Optional, non-prod override: skip Clever's school picker by pinning the
    // sandbox district. Only sent for Clever, and only when configured — the
    // key is omitted entirely otherwise (never `district_id=`).
    const extraParams = {};
    if (
      provider === SocialAccountProvider.clever &&
      cleverConfig.sandboxDistrictId
    ) {
      extraParams.district_id = cleverConfig.sandboxDistrictId;
    }
    const response = await authApi.getSocialAuthLink(
      provider,
      state,
      getCallbackUrl(provider),
      extraParams,
    );
    return response.data.url;
  };

  const getSocialAuthCode = async (provider) => {
    const popup = openPopup(''); //Popup should be opened right after user action, otherway it can be blocked by browser (safari, FF)
    if (!popup) {
      return;
    }

    try {
      const url = await socialAuthLink(provider);
      if (!url) {
        throw new Error('Unknown provider');
      }
      popup.location.href = url;
    } catch (error) {
      logger.info(`[AuthContext] getSocialAuthCode error: ${error}`);
      popup.close();
    }
    const config = {
      msgType: 'auth',
      popup,
    };
    // eslint-disable-next-line no-unused-vars
    const { code, state } = await runPopup(config);
    return code;
  };

  const signUpWithSocialCode = async (provider, fields) => {
    const issuedAt = Date.now();
    const { data } = await authApi.signUpWithSocialCode(provider, {
      ...fields,
      redirect_uri: getCallbackUrl(provider),
    });
    await storeAuthResponse(data, issuedAt, provider);
    return data;
  };

  const signInWithSocialCode = async (provider, code) => {
    const issuedAt = Date.now();
    const { data } = await authApi.signInWithSocialCode(
      provider,
      code,
      getCallbackUrl(provider),
    );
    await storeAuthResponse(data, issuedAt, provider);
    return data;
  };

  const linkWithSocialCode = async (provider, code) => {
    return await authApi.linkWithSocialCode(
      provider,
      code,
      getCallbackUrl(provider),
    );
  };

  const unlinkSocial = async (provider) => {
    return await authApi.unlinkSocial(provider);
  };

  const refreshUserSettings = async () => {
    const settings = await clientApi.getUserSettings();
    setUser((user) => _.merge(user, { settings }));
  };

  useEffect(() => {
    const initialize = async () => {
      //Setup auto logout
      const onError = (error) => {
        if (error.response?.status === 401) {
          if (authApi.isRefreshTokenUrl(error.config.url)) {
            logout({ sendAuthEventLog: false });
          } else if (authApi.isAuthUrl(error.config.url)) {
            throw error;
          } else {
            const storedRefreshToken = getRefreshToken();
            refetchData(storedRefreshToken, STORE_AUTH_SOURCE.UNAUTHORIZED);
          }
        }

        return Promise.reject({
          status: error.response?.status,
          message:
            error.response?.data?.message ||
            error.message ||
            'Something went wrong',
        });
      };
      allApi.forEach((api) =>
        api.interceptors.response.use((response) => response, onError),
      );

      const storedRefreshToken = getRefreshToken();
      // On a top-level social auth callback (portal/IdP-initiated login) the
      // page may still hold a previous user's tokens — typical on shared
      // devices. Restoring that session here would race the incoming sign-in
      // and could overwrite the new user's session, so skip the restore and
      // let the callback establish the session for whoever just authenticated.
      if (storedRefreshToken && !isTopLevelSocialAuthCallback()) {
        await refetchData(storedRefreshToken, STORE_AUTH_SOURCE.INIT);
      }
      setIsInited(true);
    };
    initialize();
  }, []);

  const socketAuthFailed = async () => {
    const storedRefreshToken = getRefreshToken();
    if (!storedRefreshToken) {
      throw new Error('No refresh token');
    }
    const issuedAt = Date.now();
    const { data } = await authApi.refreshToken(storedRefreshToken);
    if (data.reason) {
      logout({ redirectPath: null });
      return navigate('/login', {
        replace: true,
        state: {
          legal_restrictions: {
            reason: data.reason,
            details: data.details,
          },
        },
      });
    }
    await storeAuthResponse(data, issuedAt, STORE_AUTH_SOURCE.UNAUTHORIZED);
  };
  const dictationScenario = user?.settings?.app?.dictation_scenario;

  return (
    <AuthContext.Provider
      value={{
        isInited,
        isAuthenticated,
        tokenIssuedAt,
        getToken,
        getRefreshToken,
        user,
        userSettingsUpdating,
        userTasks,
        signUp,
        login,
        logout,
        resetPassword,
        updateUser,
        updateDictationScenario,
        updateUserSettings,
        completeUserTask,
        fetchUserTasks,
        dictationScenario,
        getSocialAuthCode,
        signUpWithSocialCode,
        signInWithSocialCode,
        linkWithSocialCode,
        unlinkSocial,
        refreshUserSettings,
        socketAuthFailed,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
